import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import phoneCover from '../../Assets/powerfeaturesection/phonecover.webp';
import card1 from '../../Assets/powerfeaturesection/card1.webp';
import card2 from '../../Assets/powerfeaturesection/card2.webp';
import card3 from '../../Assets/powerfeaturesection/card3.webp';
import card4 from '../../Assets/powerfeaturesection/card4.webp';
import card5 from '../../Assets/powerfeaturesection/card5.webp';

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
    {
    id: '01',
    badge: 'Leads & Inquiries',
    heading: 'Every Lead Captured. None Left in WhatsApp.',
    description: `The best studios don't run on memory — they run on systems. Share your public inquiry form link on Instagram, your website, and WhatsApp. Every lead arrives in your pipeline with their event type, date, location, and contact captured and structured. Set automated follow-up sequences so every inquiry gets a response, even when you're on a shoot. Convert leads into bookings with a quote generated in minutes.`,
    bullets: [
      'Public inquiry form — shareable link, zero manual entry',
      'Structured lead cards with event date, type & location',
      'Automated WhatsApp follow-up sequences for every lead',
    ],
    screen: card3,
    bgColor: '#f0fff2',
    stripBgColor: '#cff5d5',
    bulletCurveColor: '#78aa80',
    side: 'left',
    phoneX: 400,
  },
   {
    id: '02',
    badge: 'Payments & Collection',
    heading: 'Get Paid Faster. Stop Chasing Clients.',
    description: 'Every rupee your studio earns should be tracked, collected, and reconciled without a single awkward call. Generate a payment link in one tap and send it directly to your client. Track advance payments, pending balances, and final settlements per booking — not just in total. Automated payment reminders go out on your schedule so you never have to follow up manually. And when a client pays their balance, their album unlocks on its own.',
    bullets: [
      'Per-booking advance, balance & settlement tracking',
      'Automated payment reminders on your own schedule',
    ],
    screen: card2,
    bgColor: '#eefaff',
    stripBgColor: '#cdf2ff',
    bulletCurveColor: '#7eb5c4',
    side: 'right',
    phoneX: -400,
  },
   {
    id: '03',
    badge: 'Quotes & Orders',
    heading: 'Professional Quotes. Tracked Orders. Every Time.',
    description: 'A studio that looks professional wins more bookings. kwikmemories lets you build a reusable product catalog — fixed charges, album types, paper options, event types — and generate a formal PDF quotation in minutes. Once a client approves, it becomes a tracked order with assigned team members, logged expenses, progress stages, and a complete income-expense ledger. From first quote to final delivery, every rupee and every task is accounted for.',
    bullets: [
      'PDF quotation export — ready to send in under 5 minutes',
      'Bookings convert to orders with full production tracking',
      'Per-order income & expense ledger — clear, split, auditable',
    ],
    screen: card5,
    bgColor: '#fff4fb',
    stripBgColor: '#f7bae2',
    bulletCurveColor: '#c683ad',
    side: 'left',
    phoneX: 400,
  },
  {
    id: '04',
    badge: 'Albums & Delivery',
    heading: 'Deliver Stunning Galleries. Protect Every Photo.',
    description: 'Your photos deserve better than a Google Drive link. kwikmemories gives every client a beautiful, branded gallery — organized into folders, accessible by a simple redeemable code, and protected exactly the way you choose. Lock full-resolution downloads until payment clears. Set per-photo pricing for paid downloads. Hide albums from public view with one tap. You stay in control, even after delivery.',
    bullets: [
      'Full-res gallery locks until balance is paid — unlocks automatically',
      'Organized into folders per function (Haldi, Baraat, Reception)',
      'Redeemable album codes — no client login needed',
    ],
    screen: card1,
    bgColor: '#fbf3ff',
    stripBgColor: '#eedfff',
    bulletCurveColor: '#9672aa',
    side: 'right',
    phoneX: -400,
  },
  {
    id: '05',
    badge: 'Team & Crew',
    heading: 'Your Whole Crew. One Screen.',
    description: `Managing a shoot means coordinating photographers, drone operators, editors, and helpers — all at once. kwikmemories lets you add every team member, assign them to specific events, and track their payouts in one place. Role-based access means your photographer sees their schedule, your sales person sees leads, and you see everything. No more "who's going where" confusion the night before a wedding.`,
    bullets: [
      'Role-based access — admin, photographer, editor, helper, sales',
      'Per-event crew assignment with role and amount',
      'Individual payout tracking — total, completed, and pending',
    ],
    screen: card4,
    bgColor: '#fff1eb',
    stripBgColor: '#ffd8ca',
    bulletCurveColor: '#b78a7d',
    side: 'left',
    phoneX: 400,
  },
 
];

// how much scroll distance (as a % of viewport height, per feature) the
// pinned section eats up before it releases. Lower = fewer scrolls needed.
const DESKTOP_SCROLL_PER_FEATURE = 260; // was 350 -> ~half the scroll distance
const MOBILE_SCROLL_TOTAL = 260; // was 520 -> ~half the scroll distance

const NewPowerFeatureSection = () => {
  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const phoneRef = useRef(null);
  const cardRefs = useRef([]);
  const screenRefs = useRef([]);
  const mobileStageRef = useRef(null);
  const mobileScreenRefs = useRef([]);
  const mobileCardRefs = useRef([]);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const stage = stageRef.current;
    const phone = phoneRef.current;
    const cards = cardRefs.current.filter(Boolean);
    const screens = screenRefs.current.filter(Boolean);
    const mobileStage = mobileStageRef.current;
    const mobileScreens = mobileScreenRefs.current.filter(Boolean);
    const mobileCards = mobileCardRefs.current.filter(Boolean);

    if (!section || !stage || !phone || cards.length !== FEATURES.length || screens.length !== FEATURES.length) {
      return undefined;
    }

    const mm = gsap.matchMedia();

mm.add('(min-width: 969px)', () => {
  const getCardTravel = () => Math.min(stage.offsetHeight * 1.18, 800);

  gsap.set(phone, { xPercent: -50, yPercent: -50, x: FEATURES[0].phoneX, y: 0 });
  gsap.set(cards, { opacity: 1, visibility: 'hidden', yPercent: -50, y: getCardTravel, scale: 1 });
  gsap.set(screens, { autoAlpha: 0, xPercent: 0, zIndex: 1, clipPath: 'inset(0 0 0 0)' });
  gsap.set(cards[0], { visibility: 'visible', yPercent: -50, y: 0, scale: 1 });
  gsap.set(screens[0], { autoAlpha: 1, xPercent: 0, zIndex: 2, clipPath: 'inset(0 0 0 0)' });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: stage,
      start: 'top top',
      end: `+=${FEATURES.length * DESKTOP_SCROLL_PER_FEATURE}%`,
      pin: true,
      scrub: 1.1,
      anticipatePin: 1,
      invalidateOnRefresh: true,
    }
  });

 const MOVE_DURATION = 1.6;

// tune these two to control how much of the transition is "old card leaving"
// vs "new card arriving" — they're percentages of MOVE_DURATION and now
// sit back-to-back (no dead gap) so the whole sweep feels continuous.
const EXIT_START_PCT = 0.05;   // old card starts leaving almost immediately
const EXIT_END_PCT = 0.72;      // old card takes until the halfway point to fully exit
const ENTER_START_PCT = 0.72;   // new card starts entering right as old one finishes
const ENTER_END_PCT = 1.0;     // new card takes the rest of the duration to settle

const EXIT_START = MOVE_DURATION * EXIT_START_PCT;
const EXIT_END = MOVE_DURATION * EXIT_END_PCT;
const ENTER_START = Math.max(MOVE_DURATION * ENTER_START_PCT, EXIT_END); // safety clamp, still guarantees no overlap
const ENTER_END = Math.max(MOVE_DURATION * ENTER_END_PCT, ENTER_START + 0.2);

const EXIT_DURATION = EXIT_END - EXIT_START;
const ENTER_DURATION = ENTER_END - ENTER_START;

FEATURES.forEach((feature, index) => {
  if (index === 0) {
    tl.to({}, { duration: 0.15 });
    return;
  }

  const previousIndex = index - 1;
  const transitionLabel = `transition-${index}`;

  // Phone moving RIGHT (phoneX increasing) → screen should wipe right-to-left.
  // Phone moving LEFT  (phoneX decreasing) → screen should wipe left-to-right (default/original).
  const movingRight = feature.phoneX > FEATURES[previousIndex].phoneX;

  const enterStartClip = movingRight ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)';
  const enterEndClip   = movingRight ? 'inset(0 0 0 0%)'   : 'inset(0 0% 0 0)';
  const exitEndClip    = movingRight ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)';

  tl.add(transitionLabel);

  // next card stays invisible+off-screen until its enter window starts
  tl.set(
    cards[index],
    {
      visibility: 'hidden',
      yPercent: -50,
      y: () => getCardTravel(),
    },
    transitionLabel
  );

  tl.set(
    screens[index],
    {
      autoAlpha: 1,
      zIndex: 3,
      clipPath: enterStartClip,
    },
    transitionLabel
  );

  // PHONE MOVE — the distance reference for the whole transition
  tl.to(
    phone,
    { x: feature.phoneX, duration: MOVE_DURATION, ease: 'power1.inOut' },
    transitionLabel
  );

  // CURRENT CARD EXIT — slides up and off, smooth ease, longer window
  tl.to(
    cards[previousIndex],
    { y: () => -getCardTravel(), duration: EXIT_DURATION, ease: 'power1.inOut' },
    `${transitionLabel}+=${EXIT_START}`
  );

  // hide old card the instant its exit finishes — hard cutoff, no overlap
  tl.set(
    cards[previousIndex],
    { visibility: 'hidden' },
    `${transitionLabel}+=${EXIT_END}`
  );

  // NEW CARD becomes visible only once exit has finished, then glides in
  tl.set(
    cards[index],
    { visibility: 'visible' },
    `${transitionLabel}+=${ENTER_START}`
  );

  tl.to(
    cards[index],
    { y: 0, duration: ENTER_DURATION, ease: 'power1.inOut' },
    `${transitionLabel}+=${ENTER_START}`
  );

  // phone-screen reveal/hide stays in sync with the full phone move
  tl.to(
    screens[index],
    { clipPath: enterEndClip, duration: MOVE_DURATION, ease: 'power1.inOut' },
    transitionLabel
  );

  tl.to(
    screens[previousIndex],
    { clipPath: exitEndClip, duration: MOVE_DURATION, ease: 'power1.inOut' },
    transitionLabel
  );

  tl.set(
    screens[previousIndex],
    { autoAlpha: 0, zIndex: 1, clipPath: 'inset(0 0 0 0)' },
    `${transitionLabel}+=${MOVE_DURATION}`
  );

  tl.to({}, { duration: 0.3 });
});

  ScrollTrigger.refresh();

  const refresh = () => ScrollTrigger.refresh();
  window.addEventListener('load', refresh);
  window.addEventListener('resize', refresh);

  return () => {
    window.removeEventListener('load', refresh);
    window.removeEventListener('resize', refresh);
    tl.scrollTrigger?.kill();
    tl.kill();
  };
});

    mm.add('(max-width: 968px)', () => {
      gsap.set([phone, ...cards, ...screens], { clearProps: 'all' });

      if (!mobileStage || mobileScreens.length !== FEATURES.length || mobileCards.length !== FEATURES.length) {
        return undefined;
      }

      gsap.set(mobileScreens, {
        autoAlpha: 1,
        xPercent: 100,
        zIndex: 1,
      });
      gsap.set(mobileCards, {
        autoAlpha: 1,
        xPercent: 0,
        yPercent: 68,
        clipPath: 'inset(0 0 0 0)',
      });

      mobileCards.forEach((card, index) => {
        gsap.set(card, { zIndex: 2 + index });
      });

      gsap.set(mobileScreens[0], {
        xPercent: 0,
        zIndex: 2,
      });

      gsap.set(mobileCards[0], {
        xPercent: 0,
        yPercent: 0,
        clipPath: 'inset(0 0 0 0)',
      });

      const mobileTl = gsap.timeline({
        scrollTrigger: {
          trigger: mobileStage,
          start: 'top top',
          end: `+=${MOBILE_SCROLL_TOTAL}%`,
          pin: true,
          pinSpacing: true,
          scrub: 0.6,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      FEATURES.forEach((_, index) => {
        if (index === 0) {
          mobileTl.to({}, { duration: 0.45 });
          return;
        }

        const previousIndex = index - 1;

        mobileTl.set(mobileScreens[index], {
          zIndex: 4 + index,
          xPercent: 100,
        });

        mobileTl.set(mobileCards[index], {
          xPercent: 0,
          yPercent: 68,
          clipPath: 'inset(0 0 0 0)',
        });

        mobileTl.to(mobileScreens[index], {
          xPercent: 0,
          duration: 0.6,
          ease: 'power2.out',
        }, '<');

        mobileTl.to(mobileScreens[previousIndex], {
          xPercent: -100,
          duration: 0.6,
          ease: 'power2.out',
        }, '<');

        mobileTl.to(mobileCards[index], {
          yPercent: 0,
          duration: 0.3,
          ease: 'power2.out',
        }, '<');

        mobileTl.to({}, { duration: 0.35 });
      });

      return () => {
        mobileTl.scrollTrigger?.kill();
        mobileTl.kill();
      };
    });

    return () => mm.revert();
  }, []);

  return (
    <SectionWrapper ref={sectionRef} id="powerful-feature">
      <SectionHeader>
        <Title>
          Powerful Features <TitleGradient>Designed for You</TitleGradient>
        </Title>
        <Subtitle>CORE FEATURES</Subtitle>
      </SectionHeader>

      <DesktopStage ref={stageRef}>
        <AnimationCanvas>
          <CardsLayer>
            {FEATURES.map((feat, index) => (
              <FeatureCard
                key={feat.id}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                $side={feat.side}
                $bgColor={feat.bgColor}
              >
                <CardHeader $stripBgColor={feat.stripBgColor}>
                  <Badge>{feat.badge}</Badge>
                  <Number>{feat.id}</Number>
                </CardHeader>
                <CardHeading>{feat.heading}</CardHeading>
                <CardDescription>{feat.description}</CardDescription>
                <BulletsGrid>
                  {feat.bullets.map((bullet) => (
                    <BulletItem key={bullet}>
                      <BulletIconWrapper>
                        <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                           <path d="M2 9H13" stroke="black" strokeWidth="2" strokeLinecap="round" />
                          <path d="M10.5 4.5L15 9L10.5 13.5" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          {/* <path d="M16.2 1.6C20.1 3.2 21.5 7 20.9 9.9C20.3 12.9 18 15.4 15.2 16.4" stroke={feat.bulletCurveColor} strokeWidth="2.4" strokeLinecap="round" /> */}
                        </svg>
                      </BulletIconWrapper>
                      <BulletText>{bullet}</BulletText>
                    </BulletItem>
                  ))}
                </BulletsGrid>
              </FeatureCard>
            ))}
          </CardsLayer>

         <SharedPhone ref={phoneRef}>
  <PhoneGlowWrap>
    <GlowCircleOuter />
    <GlowCircleInner />
  </PhoneGlowWrap>

  <PhoneScreenMask>
    {FEATURES.map((feat, index) => (
      <PhoneScreenImage
        key={feat.id}
        ref={(el) => { screenRefs.current[index] = el; }}
        src={feat.screen}
        alt={`${feat.badge} phone screen`}
      />
    ))}
  </PhoneScreenMask>

  <PhoneCover src={phoneCover} alt="Mobile mockup" />
</SharedPhone>
        </AnimationCanvas>
      </DesktopStage>

      <MobileFallback>
        <MobileScrollStage ref={mobileStageRef}>
          <MobileSinglePhone>
            <PhoneScreenMask>
              {FEATURES.map((feat, index) => (
                <MobilePhoneScreenImage
                  key={feat.id}
                  ref={(el) => {
                    mobileScreenRefs.current[index] = el;
                  }}
                  src={feat.screen}
                  alt={`${feat.badge} phone screen`}
                />
              ))}
            </PhoneScreenMask>
            <PhoneCover src={phoneCover} alt="Mobile mockup" />
          </MobileSinglePhone>

          <MobileCardViewport>
            {FEATURES.map((feat, index) => (
              <MobileChangingCard
                key={feat.id}
                ref={(el) => {
                  mobileCardRefs.current[index] = el;
                }}
                $bgColor={feat.bgColor}
              >
                <CardHeader $stripBgColor={feat.stripBgColor}>
                  <Badge>{feat.badge}</Badge>
                  <Number>{feat.id}</Number>
                </CardHeader>
                <CardHeading>{feat.heading}</CardHeading>
                <CardDescription>{feat.description}</CardDescription>
              </MobileChangingCard>
            ))}
          </MobileCardViewport>
        </MobileScrollStage>
      </MobileFallback>
    </SectionWrapper>
  );
};

export default NewPowerFeatureSection;

const SectionWrapper = styled.section`
  padding: 80px 64px 4px;
  background: #ffffff;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: 60px 24px 40px;
  }

  @media (max-width: 768px) {
    padding: 32px 16px 24px;
  }

  @media (max-width: 480px) {
    padding: 24px 12px 16px;
  }

  @media (min-width: 1600px) {
    padding: 120px 100px 80px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;

  @media (min-width: 969px) {
    margin-bottom: 8px;
  }

  @media (max-width: 768px) {
    margin-bottom: 48px;
  }

  @media (min-width: 1600px) {
    margin-bottom: 80px;
  }
`;

const Title = styled.h2`
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  font-size: 64px;
  color: #121212;
  margin: 0 0 -2px;
  line-height: 1.2;

  @media (min-width: 969px) {
    line-height: 1;
    margin-bottom: 0px;
  }

  @media (max-width: 1024px) {
    font-size: 48px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 1.3;
  }

  @media (max-width: 420px) {
    font-size: 28px;
  }

  @media (max-width: 360px) {
    font-size: 24px;
  }

  @media (min-width: 1600px) {
    font-size: 80px;
  }
`;

const TitleGradient = styled.span`
  font-style: italic;
  font-weight: 400;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`;

const Subtitle = styled.div`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-size: 28px;
  font-weight: 500;
  line-height: 140%;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #121212;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  &::before, &::after {
    content: '•';
    background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (min-width: 1600px) {
    font-size: 34px;
  }
`;

const DesktopStage = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #ffffff;
  padding-top: 8px;

  @media (max-width: 968px) {
    display: none;
  }
`;

const AnimationCanvas = styled.div`
  position: relative;
  width: min(1240px, 100%);
  height: min(660px, calc(100vh - 40px));
  min-height: 460px;
  margin: 0 auto;

  @media (min-width: 1440px) {
    width: min(1440px, 100%);
    height: min(800px, calc(100vh - 40px));
  }

  @media (min-width: 1600px) {
    width: min(1600px, 100%);
    height: min(800px, calc(100vh - 40px));
  }
`;

const CardsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const FeatureCard = styled.div`
  position: absolute;
  top: 56%;
  left: ${props => (props.$side === 'left' ? '0' : 'auto')};
  right: ${props => (props.$side === 'right' ? '0' : 'auto')};
  width: min(42vw, 520px);
  min-height: 360px;
  background: ${props => props.$bgColor};
  border: none;
  border-radius: 16px;
  padding: 42px;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.219);
  will-change: transform, opacity;

  @media (min-width: 1440px) {
    width: min(44vw, 580px);
  }

  @media (min-width: 969px) {
    top: 52%;
  }

  @media (max-width: 1024px) {
    min-height: 300px;
  }

  @media (min-width: 1600px) {
    width: 620px;
    min-height: 420px;
    padding: 54px;
  }
`;

const StaticCard = styled.div`
  width: 100%;
  background: ${props => props.$bgColor};
  border: none;
  border-radius: 16px;
  padding: 28px 24px;
  box-sizing: border-box;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 34px;
  background: ${props => props.$stripBgColor || 'transparent'};
  border-radius: 8px;
  padding: 20px 26px;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 16px 18px;
    margin-bottom: 24px;
  }
`;

const Badge = styled.span`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #121212;
  line-height: 120%;
  letter-spacing: 0;

  @media (max-width: 480px) {
    font-size: 17px;
  }

  @media (min-width: 1600px) {
    font-size: 25px;
  }
`;

const Number = styled.span`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #121212;
  line-height: 120%;
  letter-spacing: 0;

  @media (min-width: 1600px) {
    font-size: 25px;
  }
`;

const CardHeading = styled.h3`
  font-family: 'Playfair Display', serif;
  font-weight: 600;
  font-size: 30px;
  color: #121212;
  margin: 0 0 18px;
  line-height: 1.18;
  letter-spacing: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (min-width: 1600px) {
    font-size: 40px;
  }
`;

const CardDescription = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #121212;
  line-height: auto;
  letter-spacing: 0;
  margin: 0 0 32px;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 0;
  }

  @media (min-width: 1600px) {
    font-size: 14px;
  }
`;

const BulletsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
  width: 100%;
`;

const BulletItem = styled.div`
  display: flex;
  align-items: start;
  gap: 12px;
`;

const BulletIconWrapper = styled.div`
  width: 22px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transform: translateY(10%);
`;

const BulletText = styled.span`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #121212;
  line-height: 135%;
  letter-spacing: 0;
`;

const SharedPhone = styled.div`
  position: absolute;
  top: 56%;
  left: 50%;
  width: clamp(220px, 15vw, 300px);
  aspect-ratio: 1116 / 2200;
  transform: none;
  z-index: 2;
  filter: drop-shadow(0 18px 24px rgba(0, 0, 0, 0.12));
  will-change: transform;

  @media (min-width: 1440px) {
    width: 250px;
  }

  @media (min-width: 1441px) {
    width: 280px;
  }

  @media (min-width: 1000px) and (max-width: 1400px) {
    width: 300px;
  }

  @media (max-width: 1024px) {
    width: 260px;
  }

  @media (min-width: 1600px) {
    width: 300px;
  }

  @media (min-width: 969px) {
    top: 52%;
  }

  @media (max-width: 968px) {
    width: clamp(160px, 18vw, 220px);
  }

  @media (max-width: 480px) {
    width: clamp(140px, 30vw, 190px);
  }
`;

const PhoneScreenMask = styled.div`
  position: absolute;
  top: 1%;
  left: 2%;
  right: 0%;
  bottom: 0%;
  width: 96%;
  height: 98%;
  border-radius: 11%;
  overflow: hidden;
  z-index: 1;
  background: #ffffff;
`;

const PhoneScreenImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
`;

const PhoneGlowWrap = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160%;
  height: 160%;
  z-index: 0;
  pointer-events: none;


  @media (max-width: 1024px) {
    width: 440px;
    height: 440px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: 320px;
    height: 320px;
  }

  @media (max-width: 767px) {
    width: 260px;
    height: 260px;
  }
`;

const GlowCircleOuter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  width: 450px;
  height: 450px;
  background: linear-gradient(140deg, #ffc951 0%, #c94daa 100%);
  opacity: 0.15;

  @media (max-width: 1024px) {
    width: 380px;
    height: 380px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: 280px;
    height: 280px;
  }

  @media (max-width: 767px) {
    width: 220px;
    height: 220px;
  }
`;

const GlowCircleInner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 350px;
  height: 350px;
  border-radius: 50%;
  background: linear-gradient(140deg, #ffc951 0%, #c94daa 100%);
  opacity: 0.25;

  @media (max-width: 1024px) {
    width: 380px;
    height: 380px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: 280px;
    height: 280px;
  }

  @media (max-width: 767px) {
    width: 220px;
    height: 220px;
  }
`;

const PhoneCover = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 2;
  pointer-events: none;
`;

const MobileFallback = styled.div`
  display: none;

  @media (max-width: 968px) {
    display: block;
    width: 100%;
  }
`;

const MobileScrollStage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  min-height: 100vh;
  justify-content: flex-start;
  padding-top: 0;

  @media (max-width: 420px) {
    gap: 10px;
  }
`;

const MobileSinglePhone = styled(SharedPhone)`
  position: relative;
  top: auto;
  left: auto;
  transform: none;
  width: min(58vw, 210px);
`;

const MobilePhoneScreenImage = styled(PhoneScreenImage)``;

const MobileCardViewport = styled.div`
  position: relative;
  width: min(92vw, 360px);
  height: 260px;
  overflow: hidden;

  @media (min-width: 600px) and (max-width: 968px) {
    width: min(80vw, 480px);
    height: 220px;
  }

  @media (max-width: 420px) {
    height: 300px;
  }
`;

const MobileChangingCard = styled(StaticCard)`
  position: absolute;
  inset: 0;
  padding: 20px 16px;
  border-radius: 14px;

  ${CardHeader} {
    margin-bottom: 18px;
    padding: 14px 14px;
  }

  ${Badge}, ${Number} {
    font-size: 13px;
  }

  ${CardHeading} {
    font-size: 24px;
    margin-bottom: 10px;
    line-height: 1.2;
  }

  ${CardDescription} {
    font-size: 14px;
    line-height: 1.4;
    margin: 0;
  }

  @media (max-width: 420px) {
    padding: 18px 14px;

    ${CardHeading} {
      font-size: 20px;
    }

    ${CardDescription} {
      font-size: 13px;
    }
  }
`;